import { DrainOutcome } from 'src/models/TranscriptOld'
import { ASRUpdateStreamMessage } from 'src/network/StreamClient'

const HEARTBEAT_INTERVAL_MS = 5_000

type AsrHeartbeat = {
    messagesReceived: number
    messagesWithWords: number
    uniqueUtteranceCount: number
    lastUttId: number | null
    lastWordCount: number | null
    drainCount: number
    avgDrainDelayMs: number
    maxDrainDelayMs: number
    maxQueueDepth: number
    totalAdded: number
    totalChanged: number
    autoScrollMode: boolean
    tabVisible: boolean
}

class AsrTelemetryService {
    private _isStopped = true
    private _intervalId: ReturnType<typeof setInterval> | null = null

    private _messagesReceived = 0
    private _messagesWithWords = 0
    private _utteranceIds = new Set<number>()
    private _lastUttId: number | null = null
    private _lastWordCount: number | null = null

    private _drainCount = 0
    private _drainDelayCount = 0
    private _drainDelaySum = 0
    private _maxDrainDelay = 0

    private _maxQueueDepth = 0
    private _currentQueueDepth = 0

    private _totalAdded = 0
    private _totalChanged = 0

    private _autoScrollMode = false
    private _tabVisible = true

    private _previousHeartbeat: AsrHeartbeat | null = null

    recordMessage = (msg: ASRUpdateStreamMessage) => {
        if (this._isStopped) return

        this._messagesReceived++

        const wordCount = msg.data.word_list.length
        if (wordCount > 0) {
            this._messagesWithWords++
        }

        this._utteranceIds.add(msg.data.utt_id)
        this._lastUttId = msg.data.utt_id
        this._lastWordCount = wordCount
    }

    recordQueueDepth = (size: number) => {
        if (this._isStopped) return

        this._currentQueueDepth = size
        if (size > this._maxQueueDepth) {
            this._maxQueueDepth = size
        }
    }

    recordDrainOutcome = (outcome: DrainOutcome) => {
        if (this._isStopped) return

        this._drainCount++
        this._totalAdded += outcome.added
        this._totalChanged += outcome.changed
    }

    recordDrainDelay = (delayMs: number) => {
        if (this._isStopped) return

        this._drainDelayCount++
        this._drainDelaySum += delayMs
        if (delayMs > this._maxDrainDelay) {
            this._maxDrainDelay = delayMs
        }
    }

    setAutoScrollMode = (mode: boolean) => {
        if (this._isStopped) return

        if (this._autoScrollMode !== mode) {
            this._autoScrollMode = mode
            console.log('[asr-telemetry] autoscroll-change', { autoScrollMode: mode })
        }
    }

    setTabVisible = (visible: boolean) => {
        if (this._isStopped) return

        if (this._tabVisible !== visible) {
            this._tabVisible = visible
            console.log('[asr-telemetry] visibility-change', {
                tabVisible: visible,
                queueDepth: this._currentQueueDepth,
            })
        }
    }

    start = () => {
        this.stop()
        this._isStopped = false
        this._intervalId = setInterval(this._flush, HEARTBEAT_INTERVAL_MS)
    }

    stop = () => {
        this._isStopped = true
        if (this._intervalId !== null) {
            clearInterval(this._intervalId)
            this._intervalId = null
        }
        this._reset()
        this._previousHeartbeat = null
    }

    private _flush = () => {
        const heartbeat: AsrHeartbeat = {
            messagesReceived: this._messagesReceived,
            messagesWithWords: this._messagesWithWords,
            uniqueUtteranceCount: this._utteranceIds.size,
            lastUttId: this._lastUttId,
            lastWordCount: this._lastWordCount,
            drainCount: this._drainCount,
            avgDrainDelayMs: this._drainDelayCount > 0 ? Math.round(this._drainDelaySum / this._drainDelayCount) : 0,
            maxDrainDelayMs: Math.round(this._maxDrainDelay),
            maxQueueDepth: this._maxQueueDepth,
            totalAdded: this._totalAdded,
            totalChanged: this._totalChanged,
            autoScrollMode: this._autoScrollMode,
            tabVisible: this._tabVisible,
        }

        if (!this._isIdentical(heartbeat)) {
            console.log('[asr-telemetry] heartbeat', heartbeat)
            this._previousHeartbeat = heartbeat
        }

        this._reset()
    }

    private _isIdentical(heartbeat: AsrHeartbeat): boolean {
        if (this._previousHeartbeat === null) return false

        const prev = this._previousHeartbeat

        return (
            heartbeat.messagesReceived === 0 &&
            prev.messagesReceived === 0 &&
            heartbeat.drainCount === 0 &&
            prev.drainCount === 0 &&
            heartbeat.autoScrollMode === prev.autoScrollMode &&
            heartbeat.tabVisible === prev.tabVisible
        )
    }

    private _reset() {
        this._messagesReceived = 0
        this._messagesWithWords = 0
        this._utteranceIds.clear()
        this._lastUttId = null
        this._lastWordCount = null
        this._drainCount = 0
        this._drainDelayCount = 0
        this._drainDelaySum = 0
        this._maxDrainDelay = 0
        this._maxQueueDepth = 0
        this._totalAdded = 0
        this._totalChanged = 0
    }
}

export const asrTelemetryService = new AsrTelemetryService()
